<script setup lang="ts">
import { useAlertStore } from '@/services/stores/alertStore';
import { computed, ref } from 'vue';
import Routes from '@/constants/Routes';
import { loginErrorMessage, resolveReturnUrl, returnUrlQuery, routeAfterLogin } from '@/helpers/loginHelpers';
import { DxForm, DxSimpleItem, DxRequiredRule, DxButtonItem, DxButtonOptions, DxGroupItem } from 'devextreme-vue/form';
import Icons from '@/constants/Icons';
import { useLoginService } from '@/services/api/loginService';
import SubmitButtonContent from '@/components/devextreme/SubmitButtonContent.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import type { IMfaRequest } from '@/types/interfaces/LoginTypes.ts';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/services/stores/authStore';

definePage({
  name: 'LoginMfa',
  meta: {
    anonymous: true
  }
});

const auth = useAuthStore();
const router = useRouter();
const route = useRoute();
const returnUrl = computed(() => resolveReturnUrl(router, route.query));
// The other login pages continue with the same page after login.
const returnQuery = computed(() => returnUrlQuery(returnUrl.value?.fullPath));
const alert = useAlertStore();
const login = useLoginService();
const loading = ref<boolean>(false);
const mfa = ref<IMfaRequest>({
  token: ''
});

async function onSubmit(): Promise<void> {
  loading.value = true;
  const response = await login.verifyToken(mfa.value);
  if (response && response.success && response.data) {
    auth.initialize(response.data.token);

    await router.push(routeAfterLogin(auth, false, returnUrl.value));
    return;
  }

  loading.value = false;
  alert.showError(loginErrorMessage(response.problem));
}
</script>

<template>
  <p class="lead text-center">
    {{ $t('login.enterCode') }}
  </p>

  <form @submit.prevent="onSubmit">
    <DxForm :disabled="loading" :form-data="mfa" label-mode="floating">
      <DxSimpleItem data-field="token" :editor-options="{ placeholder: '123 456' }">
        <DxRequiredRule :message="$t('validation.required', { field: $t('fields.token') })" />
      </DxSimpleItem>

      <DxGroupItem :col-count="2">
        <DxButtonItem>
          <DxButtonOptions template="sendButtonTemplate" type="primary" :use-submit-behavior="true" width="100%" />
        </DxButtonItem>
        <DxButtonItem>
          <DxButtonOptions template="cancelButtonTemplate" width="100%" />
        </DxButtonItem>
      </DxGroupItem>

      <DxSimpleItem css-class="text-center">
        <i18n-t keypath="login.noDevice" scope="global">
          <template #link>
            <RouterLink :to="{ name: Routes.Login.MfaRecovery, query: returnQuery }">{{ $t('login.noDeviceLink') }}</RouterLink>
          </template>
        </i18n-t>
      </DxSimpleItem>

      <template #sendButtonTemplate>
        <SubmitButtonContent :loading="loading" :icon="Icons.SignInAlt" :text="$t('mfa.verifyToken')" />
      </template>

      <template #cancelButtonTemplate>
        <ButtonContent :text="$t('common.cancel')" :icon="Icons.Ban" :to="{ name: Routes.Login.Root, query: returnQuery }" />
      </template>
    </DxForm>
  </form>
</template>

<style scoped>

</style>